import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Image,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import {
  editBtn,
  expandArrow,
  expandArrowWhite,
  copyFile,
  deleteBtn,
  scrollDownArrowIcon,
  blackInfo,
  backBtn,
} from "../../../assets/images/images";
import DeleteJob from "../../modals/DeleteJob";
import { useAppDispatch } from "../../../store/hooks";
import SpinnerLoader from "../../common/SpinnerLoader";
import { ensureListHTML, handleSuccess, getValue } from "../../../utility/helpers";
import { NUMBERS } from "../../../resources/number-constants";
import { duplicateJobData } from "../../../store/features/dashboard";

const JobDataHeading: any = {
  title: "Job Title",
  about: "About",
  alternative_job_title: "Alternate job titles",
  responsibilities: "What are the day-to-day responsibilities of the role?",
  value_plus_goals: "What are the value-plus goals of this position?",
  skills: "Skills",
  industries: "Industries",
  experience: "Years of experience",
  education: "Education",
  certificate: "Licenses & Certifications",
  language: "Spoken Language",
  work_on: "What would you be working on?",
  work_approach: "How do you approach your work?",
  prior_company_name: "Prior company name brand advantage",
  citizenship_work_authorization: "Citizenship / Work authorization",
  security_clearance: "Security clearance",
  location: "Location",
  timezone: "Time Zone",
  distance: "Distance",
  employment_type: "Employment Type",
};

const JobDescriptionSummary = ({
  jobData,
  aboutFor,
  loading,
  handleClose,
  assignedTierFromParent,
}: any) => {
  const attrBackClassVar = "attribute-back-btn";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isSavedJob = aboutFor === "saved";
  const [aboutShow, setAboutShow] = useState(false);
  const [actionKey, setActionKey] = useState("");
  const [modalData, setModalData] = useState<any>(null);
  const [showDeleteJob, setShowDeleteJob] = useState(false);
  const [workMode, setWorkMode] = useState("");
  
  // Use tier data from parent if available, otherwise use cached data as fallback
  const getCachedTierData = () => {
    try {
      const tierData = getValue('tierData');
      if (tierData) {
        return JSON.parse(tierData);
      }
    } catch (error) {
      console.info('Error parsing cached tier data:', error);
    }
    return null;
  };
  const cachedTierData = getCachedTierData();
  const [assignedTier, setAssignedTier] = useState<any>(assignedTierFromParent || cachedTierData);
  // If we have cached tier data with meaningful properties, we can allow operations immediately
  // Otherwise, wait for tier data to load (tierLoading = true)
  const hasValidCachedTier = (assignedTierFromParent && typeof assignedTierFromParent === 'object' && Object.keys(assignedTierFromParent).length > 0) ||
                             (cachedTierData && typeof cachedTierData === 'object' && Object.keys(cachedTierData).length > 0);
  const [tierLoading, setTierLoading] = useState(!hasValidCachedTier);

  const [showJobDefault, setShowJobDefault] = useState(true);

  const scrollRef = useRef<any>(null);
  const [atBottom, setAtBottom] = useState(false);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
      setAtBottom(true);
    }
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setAtBottom(false);
    }
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight === scrollHeight) {
      setAtBottom(true);
    } else if (scrollTop === 0) {
      setAtBottom(false);
    }
  };

  const handleAboutClose = () => setAboutShow(false);

  const navigateTo = (route: any) => {
    navigate(route);
  };

  const handleOpen = (data: any, key: any) => {
    setActionKey(key);
    setModalData(data);
    setAboutShow(true);
  };

  const refreshJobsList = () => {
    navigateTo("/dashboard");
  };

  const handleDuplicate = async () => {
    try {
      const res = await dispatch(
        duplicateJobData({ id: jobData?.id })
      ).unwrap();
      handleSuccess(res?.detail);
      navigateTo(
        `/job-description-summary?jobId=${res?.data?.job_id}&about=duplicateSaved`
      );
    } catch (error) {
      console.info("Error during fetching user details:", error);
    }
  };

  // Sync with parent tier data when it changes
  useEffect(() => {
    if (assignedTierFromParent && typeof assignedTierFromParent === 'object' && Object.keys(assignedTierFromParent).length > 0) {
      setAssignedTier(assignedTierFromParent);
      setTierLoading(false);
    }
  }, [assignedTierFromParent]);

  // Removed fetchUserDetails - parent component (JobDescriptionPage) handles fetching
  // Data flows from parent -> Description -> JobDescriptionSummary via props

  // Effect to add/remove class from body based on tab selection
  useEffect(() => {
    if (!showJobDefault) {
      document.body.classList.add(attrBackClassVar);
    } else {
      document.body.classList.remove(attrBackClassVar);
    }
    // Clean up function to remove class on component unmount
    return () => {
      document.body.classList.remove(attrBackClassVar);
    };
  }, [showJobDefault]);

  useEffect(() => {
    if (jobData?.work_mode) {
      const flatValue = jobData?.work_mode?.title?.flat();
      setWorkMode(flatValue[0]);
    }
  }, [jobData]);

  // Removed useEffect that called fetchUserDetails - parent component handles fetching
  // Data is passed via assignedTierFromParent prop

  const renderButton = () => {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>View Detail</Tooltip>}>
        <div className="render-btn-flex">
          <Image
            src={expandArrow}
            height={12}
            width={12}
            alt="arrow"
            className="brown-arrow"
          />
          <Image
            src={expandArrowWhite}
            height={12}
            width={12}
            alt="arrow"
            className="white-arrow d-none"
          />
        </div>
      </OverlayTrigger>
    );
  };

  return (
    <>
      {!loading && (
        <>
          <div className="outer-section">
            {isSavedJob && (
              <>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Back</Tooltip>}
                >
                  <div
                    className="back-btn go-back mb-10"
                    onClick={() => handleClose()}
                  >
                    <Image
                      src={backBtn}
                      height={15}
                      width={15}
                      alt="Back"
                      className="back-btn-arrow"
                    />
                  </div>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Duplicate</Tooltip>}
                >
                  <div
                    className="back-btn copy-btn mb-10"
                    onClick={() => handleDuplicate()}
                  >
                    <Image
                      src={copyFile}
                      height={15}
                      width={15}
                      alt="copy"
                      className="back-btn-arrow"
                    />
                  </div>
                </OverlayTrigger>
              </>
            )}

            <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
              <div
                className="back-btn mb-10"
                onClick={() =>
                  navigateTo(
                    `/job-description?jobId=${jobData?.id}&action=edit&about=${aboutFor}`
                  )
                }
              >
                <Image
                  src={editBtn}
                  height={23}
                  width={23}
                  alt="Edit"
                  className="back-btn-arrow"
                />
              </div>
            </OverlayTrigger>

            {isSavedJob && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{(tierLoading || assignedTier?.mask === true) ? (tierLoading ? "Loading..." : "Delete disabled - upgrade plan to enable") : "Delete"}</Tooltip>}
              >
                <div
                  className="back-btn"
                  onClick={() => {
                    // Prevent deletion if tier data is still loading (race condition fix)
                    if (tierLoading) {
                      return; // Wait for tier data to load
                    }
                    // Check if mask is enabled - if so, don't allow deletion
                    if (assignedTier?.mask === true) {
                      return; // Do nothing - delete is disabled
                    }
                    setShowDeleteJob(true);
                  }}
                  style={{
                    cursor: (tierLoading || assignedTier?.mask === true) ? 'not-allowed' : 'pointer',
                    opacity: (tierLoading || assignedTier?.mask === true) ? 0.5 : 1
                  }}
                >
                  <Image
                    src={deleteBtn}
                    height={23}
                    width={23}
                    alt="delete"
                    className="back-btn-arrow"
                  />
                </div>
              </OverlayTrigger>
            )}
          </div>

          <div className="preview-title-responsive">
            <div className="preview-title">
              <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                <div
                  className="back-btn"
                  onClick={() =>
                    navigateTo(
                      `/job-description?jobId=${jobData?.id}&action=edit&about=${aboutFor}`
                    )
                  }
                >
                  <Image
                    src={editBtn}
                    height={23}
                    width={23}
                    alt="Edit"
                    className="back-btn-arrow"
                  />
                </div>
              </OverlayTrigger>
              {jobData?.title} <span>.</span>
            </div>
            <div className="badge-container">
              <Badge bg="secondary">{jobData?.company_name}</Badge>
            </div>

            <ul className="reviews-tabs">
              <li
                className={showJobDefault ? "reviews-tab-active" : ""}
                onClick={() => setShowJobDefault(true)}
              >
                Job description
              </li>
              <li
                className={!showJobDefault ? "reviews-tab-active" : ""}
                onClick={() => setShowJobDefault(false)}
                id="attribute-back-btn"
              >
                Attributes
              </li>
            </ul>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`preview-left ${showJobDefault ? "" : "preview-mobile-hide"} preview-left-review page-scroll`}
          >
            <div className="preview-title d-none d-md-block">
              <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                <div
                  className="back-btn"
                  onClick={() =>
                    navigateTo(
                      `/job-description?jobId=${jobData?.id}&action=edit&about=${aboutFor}`
                    )
                  }
                >
                  <Image
                    src={editBtn}
                    height={23}
                    width={23}
                    alt="Edit"
                    className="back-btn-arrow"
                  />
                </div>
              </OverlayTrigger>
              {jobData?.title} <span>.</span>
            </div>
            <div className="badge-container d-none d-md-block">
              <Badge bg="secondary">{jobData?.company_name}</Badge>
            </div>
            <div
              className={`scroll-down-btn ${atBottom ? "scroll-up" : "scroll-down"}`}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>{atBottom ? "Go to Top" : "Go to Bottom"}</Tooltip>
                }
              >
                <Button
                  className="scroll-down"
                  onClick={atBottom ? scrollToTop : scrollToBottom}
                >
                  <Image
                    src={scrollDownArrowIcon}
                    height={48}
                    width={48}
                    alt="arrow"
                    className="scroll-down-arrow"
                  />
                </Button>
              </OverlayTrigger>
            </div>
            <div className="scroll-container">
              <div className="content">
                <h2>About</h2>
                <div dangerouslySetInnerHTML={{ __html: jobData?.about }} />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() => handleOpen(jobData?.about, "about")}
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="header-with-text new-header-text">
              <h2>
                What would you work on? <span className="line"></span>
              </h2>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Job title</h4>
                  {jobData?.job_title_ranking > 0 && (
                    <div
                      className={`text ${jobData?.job_title_ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.job_title_ranking || 0}%
                    </div>
                  )}
                </div>
                <p>{jobData?.title}</p>
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() => handleOpen(jobData?.title, "title")}
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Alternate job titles</h4>
                  {Number(jobData?.alternative_job_title?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.alternative_job_title?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.alternative_job_title?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.alternative_job_title?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.alternative_job_title?.title,
                      "alternative_job_title"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>What are the day-to-day responsibilities of the role?</h4>
                  {Number(jobData?.responsibilities?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.responsibilities?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.responsibilities?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.responsibilities?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.responsibilities?.title,
                      "responsibilities"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>What are the value-plus goals of this position?</h4>
                  {Number(jobData?.value_plus_goals?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.value_plus_goals?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.value_plus_goals?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.value_plus_goals?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.value_plus_goals?.title,
                      "value_plus_goals"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="header-with-text new-header-text">
              <h2>
                What makes you a great candidate? <span className="line"></span>
              </h2>
            </div>
            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Skills</h4>
                  {Number(jobData?.skills?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.skills?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.skills?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: jobData?.skills?.title }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() => handleOpen(jobData?.skills?.title, "skills")}
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            {jobData?.industries?.title && (
              <div className="scroll-container no-scroll">
                <div className="content">
                  <div className="title-with-text">
                    <h4>Industries</h4>
                    {Number(jobData?.industries?.ranking) > 0 && (
                      <div
                        className={`text ${jobData?.industries?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                      >
                        <span className="text-ranking">Near Peer Ranking:</span>
                        {jobData?.industries?.ranking || 0}%
                      </div>
                    )}
                  </div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: jobData?.industries?.title,
                    }}
                  />
                </div>
                <div className="bottom-btn">
                  <Button
                    variant="light"
                    onClick={() =>
                      handleOpen(jobData?.industries?.title, "industries")
                    }
                  >
                    {renderButton()}
                  </Button>
                </div>
              </div>
            )}

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Years of experience</h4>
                  {Number(jobData?.experience?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.experience?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.experience?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.experience?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.experience?.title, "experience")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Education</h4>
                  {Number(jobData?.education?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.education?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.education?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.education?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.education?.title, "education")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Licenses & Certifications</h4>
                  {Number(jobData?.certificate?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.certificate?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.certificate?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.certificate?.title || '<ul><li>Licenses & Certifications not available</li></ul>',
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.certificate?.title, "certificate")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Spoken Language</h4>
                  {Number(jobData?.language?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.language?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.language?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: jobData?.language?.title }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.language?.title, "language")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>What would you be working on?</h4>

                  {Number(jobData?.work_on?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.work_on?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.work_on?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: jobData?.work_on?.title }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() => handleOpen(jobData?.work_on?.title, "work_on")}
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>How do you approach your work?</h4>
                  {Number(jobData?.work_approach?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.work_approach?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.work_approach?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.work_approach?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.work_approach?.title, "work_approach")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Prior company name brand advantage</h4>
                  {Number(jobData?.prior_company_name?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.prior_company_name?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.prior_company_name?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.prior_company_name?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.prior_company_name?.title,
                      "prior_company_name"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Citizenship / Work authorization</h4>
                  {Number(jobData?.citizenship_work_authorization?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.citizenship_work_authorization?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.citizenship_work_authorization?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.citizenship_work_authorization?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.citizenship_work_authorization?.title,
                      "citizenship_work_authorization"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Security clearance</h4>
                  {Number(jobData?.security_clearance?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.security_clearance?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.security_clearance?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: jobData?.security_clearance?.title,
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.security_clearance?.title,
                      "security_clearance"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="header-with-text new-header-text how-work-text">
              <h2>
                How would you work? <span className="line"></span>
              </h2>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Employment type</h4>
                  {Number(jobData?.employment_type?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.employment_type?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.employment_type?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ensureListHTML(jobData?.employment_type?.title),
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(
                      jobData?.employment_type?.title,
                      "employment_type"
                    )
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Work from</h4>
                  {Number(jobData?.work_mode?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.work_mode?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.work_mode?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: ensureListHTML(workMode) }}
                />
              </div>
            </div>

            {workMode &&
              (workMode.toLowerCase() === "hybrid" ||
                workMode.toLowerCase() === "flexible") && (
                <div className="scroll-container no-scroll">
                  <div className="content">
                    <div className="title-with-text">
                      <h4>Hybrid/Flexible Schedule</h4>
                      {jobData?.hybrid_schedule?.ranking > 0 && (
                        <div
                          className={`text ${jobData?.hybrid_schedule?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                        >
                          <span className="text-ranking">
                            Near Peer Ranking:
                          </span>
                          {jobData?.hybrid_schedule?.ranking || 0}%
                        </div>
                      )}
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: ensureListHTML(
                          jobData?.hybrid_schedule?.title[0]
                        ),
                      }}
                    />
                  </div>
                  <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.hybrid_schedule?.title[0], "hybrid_schedule")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
                </div>
              )}

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Location</h4>
                  {Number(jobData?.location?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.location?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.location?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ensureListHTML(jobData?.location?.title),
                  }}
                />
              </div>

              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.location?.title, "location")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4 className="align-items-center d-flex">
                    Distance
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Distances are calculated using your office location to
                          ensure precision.
                        </Tooltip>
                      }
                    >
                      <div className="info-icon ms-1">
                        <Image
                          src={blackInfo}
                          height={20}
                          width={20}
                          alt="Info"
                        />
                      </div>
                    </OverlayTrigger>
                  </h4>
                  {jobData?.distance?.ranking > 0 && (
                    <div
                      className={`text ${jobData?.distance?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.distance?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ensureListHTML(jobData?.distance?.title),
                  }}
                />
              </div>
              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.distance?.title, "distance")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>

            <div className="scroll-container no-scroll">
              <div className="content">
                <div className="title-with-text">
                  <h4>Time zone</h4>
                  {Number(jobData?.timezone?.ranking) > 0 && (
                    <div
                      className={`text ${jobData?.timezone?.ranking > NUMBERS.FIFTY ? "text-green" : "text-danger"}`}
                    >
                      <span className="text-ranking">Near Peer Ranking:</span>
                      {jobData?.timezone?.ranking || 0}%
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: ensureListHTML(jobData?.timezone?.title),
                  }}
                />
              </div>

              <div className="bottom-btn">
                <Button
                  variant="light"
                  onClick={() =>
                    handleOpen(jobData?.timezone?.title, "timezone")
                  }
                >
                  {renderButton()}
                </Button>
              </div>
            </div>
          </div>

          <div
            className={`preview-right ${showJobDefault ? "preview-mobile-hide" : ""} preview-right-review`}
          >
            <div className="right-box-container">
              <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                <div
                  className="back-btn small"
                  onClick={() =>
                    navigateTo(
                      `/idealize-candidate?jobId=${jobData?.id}&action=edit&about=${aboutFor}`
                    )
                  }
                >
                  <Image src={editBtn} height={16} width={16} alt="Edit" />
                </div>
              </OverlayTrigger>
              <div className="content">
                {jobData?.must_have?.title?.length > 0 && (
                  <>
                    <h4 className="list-title">Must-Haves</h4>
                    <ul className="check-list">
                      {jobData?.must_have?.title.map(
                        (item: any, index: any) => <li key={index}>{item}</li>
                      )}
                    </ul>
                  </>
                )}
                {jobData?.should_have?.title?.length > 0 && (
                  <>
                    <h4 className="list-title">Should-Haves</h4>
                    <ul className="check-list">
                      {jobData?.should_have?.title.map(
                        (item: any, index: any) => <li key={index}>{item}</li>
                      )}
                    </ul>
                  </>
                )}
                {jobData?.good_to_have?.title?.length > 0 && (
                  <>
                    <h4 className="list-title">Good-to-Haves</h4>
                    <ul className="check-list">
                      {jobData?.good_to_have?.title.map(
                        (item: any, index: any) => <li key={index}>{item}</li>
                      )}
                    </ul>
                  </>
                )}
                {jobData?.wont_have?.title?.length > 0 && (
                  <>
                    <h4 className="list-title">Won't-Haves</h4>
                    <ul className="check-list">
                      {jobData?.wont_have?.title.map(
                        (item: any, index: any) => <li key={index}>{item}</li>
                      )}
                    </ul>
                  </>
                )}
                {jobData?.could_have?.title?.length > 0 && (
                  <>
                    <h4 className="list-title">Could-Haves</h4>
                    <ul className="check-list">
                      {jobData?.could_have?.title.map(
                        (item: any, index: any) => <li key={index}>{item}</li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {loading && (
        <div className="summary-loader">
          <SpinnerLoader />
        </div>
      )}

      <Modal show={aboutShow} centered className="about-modal">
        <Modal.Header>{JobDataHeading[actionKey]}</Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: modalData }} />
          <div className="footer-btn">
            <Button
              variant="link"
              className="text-danger"
              onClick={handleAboutClose}
            >
              CLOSE
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {showDeleteJob && (
        <DeleteJob
          showDeleteJob={showDeleteJob}
          setShowDeleteJob={setShowDeleteJob}
          refreshJobsList={refreshJobsList}
          deleteJobId={jobData?.id}
        />
      )}
    </>
  );
};

export default JobDescriptionSummary;
